<script setup lang="ts">
import { computed } from 'vue'
import {
    ECardPhotoSizeDesktop,
    ECardSizeMobile,
    ECardsListLayout,
    ESeoHeadingSize,
    type ICardPhotoProps,
} from '../ts/types'
import { scrollTo } from '../../../utils/scrollTo'
import { useImage } from '../../../composables/useImageUrl'
import { getLinkTarget } from '../../../utils/linkTarget'
import type { ICTABlockAction } from '../../CTABlock/ts/types'
import { NuxtLink } from '#components'
import VCarouselSlide from '@/ui/plushComponents/VCarousel/VCarouselSlide.vue'
import { useDrawer } from '@/cms-ui/composables/useDrawer'
import { useClickTracking } from '@/cms-ui/composables/useAnalytics'
import { EClickElement } from '@/cms-ui/utils/dataLayer'

const props = withDefaults(defineProps<ICardPhotoProps>(), {
    seoHeadingSize: ESeoHeadingSize.H2,
})

const { image: responsiveImage } = useImage(props.image, props.page)

const mobileCardSize =
    props.mobileLayout === ECardsListLayout.Grid
        ? {
              [ECardSizeMobile.Small]:
                  'max-md:!basis-[calc(50%-0.25rem)] md:max-lg:!basis-[calc(25%-0.75rem)] md:max-lg:!max-w-full max-md:!max-w-full',
              [ECardSizeMobile.Large]:
                  'max-md:!basis-full md:max-lg:!basis-[calc(50%-0.5rem)] md:max-lg:!max-w-full max-md:!max-w-full',
          }
        : {}

const desktopCardSize =
    props.desktopLayout === ECardsListLayout.Grid
        ? {
              [ECardPhotoSizeDesktop.Small]: 'lg:!basis-[calc(25%-1.125rem)]',
              [ECardPhotoSizeDesktop.Large]: 'lg:!basis-[calc(33.3%-1rem)]',
              [ECardPhotoSizeDesktop.XLarge]: 'lg:!basis-[calc(50%-0.75rem)]',
              [ECardPhotoSizeDesktop.XXLarge]: 'lg:!basis-full',
              [ECardPhotoSizeDesktop.Fluid]: '',
          }
        : {}

const component = computed(() => {
    if (props.action?.url) {
        return 'a'
    } else if (props.action) {
        return 'button'
    } else {
        return 'div'
    }
})

const { openDrawer } = useDrawer()

function handleClick(action?: ICTABlockAction) {
    if (action?.drawer) {
        openDrawer(action.drawer, props.page)
    }
    if (action?.anchor) {
        scrollTo(action?.anchor)
    }
}

const clickHandler = computed(() => {
    if (component.value !== 'div') {
        return [
            () =>
                useClickTracking({
                    label: props.title,
                    clickPath: props.action?.url || '',
                    clickElement: EClickElement.BUTTON,
                    componentName: props.title || 'PhotoCard',
                    componentCategory: 'PhotoCard',
                }),
            () => handleClick(props.action),
        ]
    }
    return undefined
})
</script>

<template>
    <VCarouselSlide
        class="group"
        :class="[
            mobileCardSize[mobileSize],
            desktopCardSize[desktopSize],
            { 'max-lg:!px-0': mobileLayout === ECardsListLayout.Grid },
            { 'lg:!px-0': desktopLayout === ECardsListLayout.Grid },
            { 'flex-1 !shrink !grow': desktopSize === ECardPhotoSizeDesktop.Fluid },
        ]"
    >
        <component
            :is="component"
            :href="action?.url"
            :data-test="dataTest"
            :target="action ? getLinkTarget(action) : undefined"
            class="rounded-container-lg relative block h-full overflow-hidden"
            @click="clickHandler"
        >
            <div class="relative h-full">
                <BaseImage
                    v-if="image"
                    class="duration-400 size-full object-cover"
                    :class="{
                        'transition-transform ease-in group-hover:scale-105 group-hover:ease-in-out': action,
                    }"
                    :image-data="responsiveImage"
                    :alt="image.altText"
                />

                <div
                    class="p-sm lg:p-md darkmode absolute bottom-0 left-0 flex size-full max-h-[140px] items-end justify-between"
                    :class="{ content: action || title, 'lg:p-sm': desktopSize === ECardPhotoSizeDesktop.Fluid }"
                >
                    <component
                        :is="seoHeadingSize"
                        class="label-lg text-comp-high-contrast line-clamp-2"
                    >
                        {{ title }}
                    </component>
                    <VIcon
                        v-if="action"
                        class="text-brand-accent-invert"
                        name="arrow-right"
                        size="m"
                    />
                </div>
            </div>
        </component>
    </VCarouselSlide>
</template>

<style scoped>
.content{background:linear-gradient(1turn,rgba(0,0,0,.678),rgba(0,0,0,.631) 14.17%,rgba(0,0,0,.581) 29.17%,transparent)}
</style>
